/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// models
import Model from "models/model/Model.js";

// utils
import ValidationUtils from "utils/validation-utils/ValidationUtils.js";

// constants
const GESTURE_HANDLING_DEFAULT = "greedy";
const UI_POSITION_DEFAULT = String("TOP_RIGHT");
const UI_POSITIONS = Array([
  String("TOP_CENTER"),
  String("TOP_LEFT"),
  String("TOP_RIGHT"),
  String("LEFT_TOP"),
  String("RIGHT_TOP"),
  String("LEFT_CENTER"),
  String("RIGHT_CENTER"),
  String("LEFT_BOTTOM"),
  String("RIGHT_BOTTOM"),
  String("BOTTOM_CENTER"),
  String("BOTTOM_LEFT"),
  String("BOTTOM_RIGHT")
]);
const UI_SIZE_DEFAULT = Number("40");

// defaults
const DEFAULT_STREETVIEW_PROPERTIES = {
  position: {
    lat: Number("0.0"),
    lng: Number("0.0"),
  },
  pov: {
    heading: Number("165.0"),
    pitch: Number("0.0"),
    zoom: Number("1.0")
  }
};

const DEFAULT_UI_PROPERTIES = {
  disable: false,
  disableFullscreenControl: false,
  disableMapTypeControl: false,
  disablePanControl: false,
  disableScaleControl: false,
  disableStreetViewControl: false,
  gestureHandling: String(GESTURE_HANDLING_DEFAULT),
  position: String(UI_POSITION_DEFAULT),
  size: Number(UI_SIZE_DEFAULT)
};

class GoogleStreetView extends Model {
  constructor({
    id = Model.getDefaultId(),
    lastUpdated = Model.getDefaultLastUpdated(),
    streetViewProperties = { ...DEFAULT_STREETVIEW_PROPERTIES },
    tags = [{ model: "GoogleMap" }],
    uiProperties = { ...DEFAULT_UI_PROPERTIES }
  } = {}) {
    super({ id, lastUpdated, tags });
    this.state = {
      ...this.state,
      streetViewProperties: {
        ...DEFAULT_STREETVIEW_PROPERTIES,
        ...streetViewProperties
      },
      uiProperties: {
        ...DEFAULT_UI_PROPERTIES,
        ...uiProperties
      }
    };
  }

  // getters
  getPosition() {
    return {
      lat: Number(this.state.streetViewProperties.position.lat),
      lng: Number(this.state.streetViewProperties.position.lng)
    };
  }
  getPov() {
    return {
      heading: Number(this.state.streetViewProperties.pov.heading),
      pitch: Number(this.state.streetViewProperties.pov.pitch),
      zoom: Number(this.state.streetViewProperties.pov.zoom)
    };
  }
  getUiDisabled() { return Boolean(this.state.uiProperties.disable); }
  getUiDisableCloseButton() { return Boolean(this.state.uiProperties.disableCloseButton); }
  getUiDisableFullscreenControl() { return Boolean(this.state.uiProperties.disableFullscreenControl); }
  getUiDisableMapTypeControl() { return Boolean(this.state.uiProperties.disableMapTypeControl); }
  getUiDisablePanControl() { return Boolean(this.state.uiProperties.disablePanControl); }
  getUiDisableScaleControl() { return Boolean(this.state.uiProperties.disableScaleControl); }
  getUiDisableStreetViewControl() { return Boolean(this.state.uiProperties.disableStreetViewControl); }
  getUiGestureHandling() { return String(this.state.uiProperties.gestureHandling); }
  getUiPosition() { return String(this.state.uiProperties.position); }
  getUiSize() { return Number(this.state.uiProperties.size); }

  // get state
  getState() {
    return {
      id: this.getId(),
      streetViewProperties: {
        position: this.getPosition(),
        pov: this.getPov(),
      },
      tags: this.getTags(),
      uiProperties: {
        disable: this.getUiDisable(),
        disableCloseButton: this.disableCloseButton(),
        disableFullscreenControl: this.getUiDisableFullscreenControl(),
        disableMapTypeControl: this.getUiDisableMapTypeControl(),
        disablePanControl: this.getUiDisablePanControl(),
        disableScaleControl: this.getUiDisableScaleControl(),
        disableStreetViewControl: this.getUiDisableStreetViewControl(),
        gestureHandling: this.getUiGestureHandling(),
        position: this.getUiPosition(),
        size: this.getUiSize()
      }
    };
  }

  // setters
  setPosition(position) {
    if(ValidationUtils.latLng(position)) {
      this.state.streetViewProperties.position = {
        lat: Number(position.lat),
        lng: Number(position.lng)
      };
      this.onChange();
    }
    return this.getPosition();
  }

  setPov(pov) {
    if(ValidationUtils.pov(pov)) {
      this.state.streetViewProperties.pov = {
        heading: Number(pov.heading),
        pitch: Number(pov.pitch),
        zoom: Number(pov.zoom)
      };
      this.onChange();
    }
    return this.getPov();
  }

  setUiDisabled(boolean) {
    if(ValidationUtils.boolean(boolean)) {
      this.state.uiProperties.disable = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisabled();
  }

  setUiDisableCloseButton(boolean) {
    if(ValidationUtils.boolean(boolean)) {
      this.state.uiProperties.disableCloseButton = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableCloseButton();
  }

  setUiDisableFullscreenControl(boolean) {
    if(ValidationUtils.boolean(boolean)) {
      this.state.uiProperties.disableFullscreenControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableFullscreenControl();
  }

  setUiDisableMapTypeControl(boolean) {
    if(ValidationUtils.boolean(boolean)) {
      this.state.uiProperties.disableMapTypeControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableMapTypeControl();
  }

  setUiDisablePanControl(boolean) {
    if(ValidationUtils.boolean(boolean)) {
      this.state.uiProperties.disablePanControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisablePanControl();
  }

  setUiDisableScaleControl(boolean) {
    if(ValidationUtils.boolean(boolean)) {
      this.state.uiProperties.disableScaleControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableScaleControl();
  }

  setUiDisableStreetViewControl(boolean) {
    if(ValidationUtils.boolean(boolean)) {
      this.state.uiProperties.disableStreetViewControl = Boolean(boolean);
      this.onChange();
    }
    return this.getUiDisableStreetViewControl();
  }

  setUiGestureHandling(gestureHandling) {
    if(ValidationUtils.string(gestureHandling)) {
      this.state.uiProperties.gestureHandling = String(gestureHandling);
      this.onChange();
    }
    return this.getUiGestureHandling();
  }

  setUiPosition(position) {
    if(ValidationUtils.string(position) && UI_POSITIONS.includes(position)) {
      this.state.uiProperties.position = String(position);
      this.onChange();
    }
    return this.getUiPosition();
  }

  setUiSize(size) {
    if(ValidationUtils.integer(size)) {
      this.state.uiProperties.size = Number(size);
      this.onChange();
    }
    return this.getUiSize();
  }
}
export default GoogleStreetView;
